import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type {
  L2CComparisonOption,
  L2CComparisonPreset,
  L2CPeriodGrain,
} from '../../../types/leadToCash';

const FALLBACK_TITLES: Record<L2CPeriodGrain, Record<L2CComparisonPreset, string>> = {
  day: {
    latest_vs_prior: 'Latest day vs prior day',
    prior_vs_before: 'Prior day vs day before',
    same_period_yoy: 'Same day last year',
    ytd_vs_prior_ytd: 'Year to date vs prior year',
    custom: 'Custom range',
  },
  week: {
    latest_vs_prior: 'Latest week vs prior week',
    prior_vs_before: 'Prior week vs week before',
    same_period_yoy: 'Same week last year',
    ytd_vs_prior_ytd: 'Year to date vs prior year',
    custom: 'Custom range',
  },
  month: {
    latest_vs_prior: 'Latest month vs prior month',
    prior_vs_before: 'Prior month vs month before',
    same_period_yoy: 'Same month last year',
    ytd_vs_prior_ytd: 'Year to date vs prior year',
    custom: 'Custom range',
  },
  year: {
    ytd_vs_prior_ytd: 'Year to date vs prior year',
    latest_vs_prior: 'Latest complete year vs prior year',
    prior_vs_before: 'Prior year vs year before',
    same_period_yoy: 'Same period last year',
    custom: 'Custom range',
  },
};

const FALLBACK_CMP_LABELS: Record<L2CPeriodGrain, Record<L2CComparisonPreset, string>> = {
  day: {
    latest_vs_prior: 'DoD',
    prior_vs_before: 'DoD',
    same_period_yoy: 'YoY',
    ytd_vs_prior_ytd: 'YTD',
    custom: 'Custom',
  },
  week: {
    latest_vs_prior: 'WoW',
    prior_vs_before: 'WoW',
    same_period_yoy: 'YoY',
    ytd_vs_prior_ytd: 'YTD',
    custom: 'Custom',
  },
  month: {
    latest_vs_prior: 'MoM',
    prior_vs_before: 'MoM',
    same_period_yoy: 'YoY',
    ytd_vs_prior_ytd: 'YTD',
    custom: 'Custom',
  },
  year: {
    ytd_vs_prior_ytd: 'YTD',
    latest_vs_prior: 'YoY',
    prior_vs_before: 'YoY',
    same_period_yoy: 'YoY',
    custom: 'Custom',
  },
};

const FALLBACK_PRESETS: Record<L2CPeriodGrain, L2CComparisonPreset[]> = {
  day: ['latest_vs_prior', 'prior_vs_before', 'same_period_yoy'],
  week: ['latest_vs_prior', 'prior_vs_before', 'same_period_yoy'],
  month: ['latest_vs_prior', 'prior_vs_before', 'same_period_yoy'],
  year: ['ytd_vs_prior_ytd', 'latest_vs_prior', 'prior_vs_before'],
};

export function fallbackOptionsForGrain(grain: L2CPeriodGrain): L2CComparisonOption[] {
  return FALLBACK_PRESETS[grain].map((preset) => ({
    preset,
    title: FALLBACK_TITLES[grain][preset],
    label: '',
    comparison_label: FALLBACK_CMP_LABELS[grain][preset],
  }));
}

export function formatComparisonOptionTitle(opt: L2CComparisonOption): string {
  const badge = opt.comparison_label ? ` (${opt.comparison_label})` : '';
  return `${opt.title}${badge}`;
}

export function normalizePresetForGrain(
  grain: L2CPeriodGrain,
  preset: L2CComparisonPreset,
  options?: L2CComparisonOption[],
): L2CComparisonPreset {
  const allowed = (options?.length ? options : fallbackOptionsForGrain(grain)).map((o) => o.preset);
  if (allowed.includes(preset)) return preset;
  return allowed[0] ?? 'latest_vs_prior';
}

export function formatPeriodRange(
  start?: string | null,
  end?: string | null,
  grain: L2CPeriodGrain = 'week',
): string {
  if (!start || !end) return '—';
  try {
    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T00:00:00`);
    if (grain === 'month') {
      return startDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    if (grain === 'day') {
      return startDate.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    if (grain === 'year') {
      const spanDays = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
      if (spanDays < 360) {
        const fmt = (d: Date) =>
          d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        return `${startDate.getFullYear()} YTD (${fmt(startDate)} – ${fmt(endDate)})`;
      }
      return startDate.toLocaleDateString(undefined, { year: 'numeric' });
    }
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  } catch {
    return `${start} – ${end}`;
  }
}

export function CompactComparisonLine({
  grain,
  comparisonLabel,
  currentPeriod,
  priorPeriod,
  prominent = false,
  labelOverride,
  titleOverride,
}: {
  grain: L2CPeriodGrain;
  comparisonLabel?: string | null;
  currentPeriod?: { start?: string | null; end?: string | null } | null;
  priorPeriod?: { start?: string | null; end?: string | null } | null;
  prominent?: boolean;
  labelOverride?: string | null;
  titleOverride?: string | null;
}) {
  if (titleOverride || labelOverride) {
    return (
      <Stack spacing={0.25}>
        {titleOverride && (
          <Typography
            variant={prominent ? 'body2' : 'caption'}
            color="text.primary"
            sx={{ lineHeight: 1.5, fontWeight: prominent ? 700 : 600 }}
          >
            {titleOverride}
          </Typography>
        )}
        {labelOverride && (
          <Typography
            variant={prominent ? 'body2' : 'caption'}
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {labelOverride}
            {comparisonLabel ? (
              <Box component="span" fontWeight={600} color="text.secondary">
                {' '}
                ({comparisonLabel})
              </Box>
            ) : null}
          </Typography>
        )}
      </Stack>
    );
  }

  const current = formatPeriodRange(currentPeriod?.start, currentPeriod?.end, grain);
  const prior = formatPeriodRange(priorPeriod?.start, priorPeriod?.end, grain);
  const label = comparisonLabel ? ` (${comparisonLabel})` : '';

  if (prominent) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        <Box component="span" fontWeight={700} color="text.primary">
          {current}
        </Box>
        {' vs '}
        <Box component="span" fontWeight={600} color="text.primary">
          {prior}
        </Box>
        {label && (
          <Box component="span" fontWeight={600} color="text.secondary">
            {label}
          </Box>
        )}
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
      <Box component="span" fontWeight={600} color="text.primary">
        {current}
      </Box>
      {' vs '}
      {prior}
      {label}
    </Typography>
  );
}

function ComparisonMenuItem({ opt }: { opt: L2CComparisonOption }) {
  return (
    <Stack spacing={0.25} sx={{ py: 0.25, maxWidth: 420 }}>
      <Typography variant="body2" fontWeight={600} lineHeight={1.35}>
        {formatComparisonOptionTitle(opt)}
      </Typography>
      {opt.label ? (
        <Typography variant="caption" color="text.secondary" lineHeight={1.35}>
          {opt.label}
        </Typography>
      ) : null}
    </Stack>
  );
}

interface PeriodSelectorProps {
  grain: L2CPeriodGrain;
  preset: L2CComparisonPreset;
  comparisonOptions: L2CComparisonOption[];
  optionsLoading?: boolean;
  onGrainChange: (grain: L2CPeriodGrain) => void;
  onPresetChange: (preset: L2CComparisonPreset) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Tighter layout when main column is narrow (e.g. chat panel open). */
  narrow?: boolean;
}

export default function PeriodSelector({
  grain,
  preset,
  comparisonOptions,
  optionsLoading = false,
  onGrainChange,
  onPresetChange,
  disabled,
  compact = false,
  narrow = false,
}: PeriodSelectorProps) {
  const optionsReady = !optionsLoading && comparisonOptions.length > 0;
  const selectValue = normalizePresetForGrain(grain, preset, optionsReady ? comparisonOptions : undefined);
  const options = optionsReady ? comparisonOptions : fallbackOptionsForGrain(grain);
  const selected = options.find((o) => o.preset === selectValue);

  return (
    <Stack
      direction={narrow ? 'column' : 'row'}
      spacing={narrow ? 1.25 : 1}
      alignItems={narrow ? 'stretch' : 'center'}
      flexWrap={narrow ? 'nowrap' : 'wrap'}
      useFlexGap
      sx={{ flex: narrow ? '1 1 auto' : undefined, minWidth: narrow ? 0 : undefined, width: narrow ? '100%' : undefined }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={grain}
        onChange={(_, value: L2CPeriodGrain | null) => {
          if (value) onGrainChange(value);
        }}
        disabled={disabled}
        sx={narrow ? { alignSelf: 'flex-start' } : undefined}
      >
        <ToggleButton value="day" sx={compact ? { px: 1.25, py: 0.35 } : undefined}>
          Day
        </ToggleButton>
        <ToggleButton value="week" sx={compact ? { px: 1.25, py: 0.35 } : undefined}>
          Week
        </ToggleButton>
        <ToggleButton value="month" sx={compact ? { px: 1.25, py: 0.35 } : undefined}>
          Month
        </ToggleButton>
        <ToggleButton value="year" sx={compact ? { px: 1.25, py: 0.35 } : undefined}>
          Year
        </ToggleButton>
      </ToggleButtonGroup>

      <FormControl
        size="small"
        sx={{
          minWidth: narrow ? 0 : compact ? 300 : 340,
          width: narrow ? '100%' : undefined,
          flex: narrow ? '1 1 auto' : undefined,
        }}
        disabled={disabled}
      >
        {!narrow && <InputLabel id="l2c-preset-label">Comparison</InputLabel>}
        <Select
          labelId="l2c-preset-label"
          label={narrow ? undefined : 'Comparison'}
          fullWidth
          value={selectValue}
          onChange={(e) => onPresetChange(e.target.value as L2CComparisonPreset)}
          displayEmpty={narrow}
          inputProps={narrow ? { 'aria-label': 'Comparison' } : undefined}
          sx={
            narrow
              ? {
                  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                  '& .MuiOutlinedInput-input': { py: 0.875 },
                }
              : undefined
          }
          renderValue={() =>
            optionsLoading ? (
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.125 }}>
                <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  Loading periods…
                </Typography>
              </Stack>
            ) : (
              (selected ? formatComparisonOptionTitle(selected) : selectValue)
            )
          }
          MenuProps={{ PaperProps: { sx: { maxWidth: 480 } } }}
          aria-busy={optionsLoading}
        >
          {options.map((opt) => (
            <MenuItem key={opt.preset} value={opt.preset} sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}>
              <ComparisonMenuItem opt={opt} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
